// Links to an exact view of the viewer: the body in focus, the moment, the
// clock's speed, the scale, the overlays that are on and where the camera
// sits. Its own hash prefix keeps it apart from the others (#earth,
// #/earth/atmosphere, #eclipse/<date>, #iss, #launch/<slug>):
//
//   #view/earth/2026-10-02T21:30Z?speed=real&scale=true&cam=40.2,15.1,3.2&show=sat,orb
//
//   speed  real, 0 (paused) or a multiplier, negative to run backwards
//   scale  true (true scale) or art (the artistic scale)
//   spread the artistic scale's slider value (how far it spreads the orbits)
//   cam    the camera from the body: azimuth and elevation in degrees (the
//          scene's J2000 ecliptic axes) and distance in body radii
//   show   overlays switched on (OVERLAYS keys)
//
// Pure: main.js reads the view and applies it (src/ui/timemachine.js wires
// the share sheet).

export const VIEW_PREFIX = 'view';

// Overlay keys in links and the sidebar toggles they mirror.
export const OVERLAYS = {
  sat: 'satellites-toggle',
  orb: 'orbits-toggle',
  con: 'constellations-toggle',
  sci: 'science-toggle',
};

const round = (v, d) => {
  const p = 10 ** d;
  return Math.round(v * p) / p;
};

// The moment as a short UTC stamp: seconds only when they are not zero.
export function formatStamp(ms) {
  const iso = new Date(Math.round(ms / 1000) * 1000).toISOString();
  const stamp = iso.slice(0, 19);
  return (stamp.endsWith(':00') ? stamp.slice(0, 16) : stamp) + 'Z';
}

export function parseStamp(text) {
  const m = /^(\d{4}-\d{2}-\d{2})(?:T(\d{2}:\d{2}(?::\d{2})?))?Z?$/i.exec(String(text || ''));
  if (!m) return null;
  const ms = Date.parse(`${m[1]}T${m[2] || '00:00'}${m[2] && m[2].length === 5 ? ':00' : ''}Z`);
  return Number.isFinite(ms) ? ms : null;
}

// Camera offset (x, y, z from the body's centre) to azimuth, elevation and
// distance in body radii.
export function cameraToSpherical(offset, radius) {
  const dist = Math.hypot(offset.x, offset.y, offset.z);
  if (!(dist > 0) || !(radius > 0)) return null;
  const az = Math.atan2(offset.z, offset.x) * 180 / Math.PI;
  const el = Math.asin(Math.max(-1, Math.min(1, offset.y / dist))) * 180 / Math.PI;
  return { az, el, dist: dist / radius };
}

export function sphericalToCamera({ az, el, dist }, radius) {
  const a = az * Math.PI / 180;
  const e = el * Math.PI / 180;
  const r = dist * radius;
  return { x: Math.cos(e) * Math.cos(a) * r, y: Math.sin(e) * r, z: Math.cos(e) * Math.sin(a) * r };
}

// Distances in radii keep three significant figures (a close ISS view is a
// few hundredths of a radius; a wide one thousands).
function formatDist(d) {
  if (d >= 100) return String(Math.round(d));
  return String(Number(d.toPrecision(3)));
}

export function encodeView({ body, ms, speed, realistic, spread, cam, overlays }) {
  const params = [];
  params.push(`speed=${speed === 'real' ? 'real' : String(round(Number(speed) || 0, 4))}`);
  params.push(`scale=${realistic ? 'true' : 'art'}`);
  if (!realistic && Number.isFinite(spread)) params.push(`spread=${round(spread, 2)}`);
  if (cam) params.push(`cam=${round(cam.az, 1)},${round(cam.el, 1)},${formatDist(cam.dist)}`);
  const on = (overlays || []).filter((k) => OVERLAYS[k]);
  if (on.length) params.push(`show=${on.join(',')}`);
  return `#${VIEW_PREFIX}/${encodeURIComponent(body || 'sun')}/${formatStamp(ms)}?${params.join('&')}`;
}

// null when the hash is not a view link or its moment does not parse.
export function decodeView(hash) {
  const m = new RegExp(`^#\\/?${VIEW_PREFIX}\\/([^/?]+)\\/([^?]+)(?:\\?(.*))?$`).exec(String(hash || ''));
  if (!m) return null;
  const ms = parseStamp(decodeURIComponent(m[2]));
  if (ms == null) return null;
  const q = new URLSearchParams(m[3] || '');
  const speedRaw = q.get('speed');
  const speed = speedRaw === 'real' || speedRaw == null ? 'real' : Number(speedRaw);
  const scale = q.get('scale');
  let cam = null;
  const camRaw = (q.get('cam') || '').split(',').map(Number);
  if (camRaw.length === 3 && camRaw.every(Number.isFinite) && camRaw[2] > 0) {
    cam = { az: camRaw[0], el: camRaw[1], dist: camRaw[2] };
  }
  const overlays = (q.get('show') || '').split(',').filter((k) => OVERLAYS[k]);
  const spread = Number(q.get('spread'));
  let body = m[1];
  try { body = decodeURIComponent(body); } catch { /* keep as is */ }
  return {
    body: body.toLowerCase(),
    ms,
    speed: Number.isFinite(speed) || speed === 'real' ? speed : 'real',
    realistic: scale == null ? null : scale === 'true',
    spread: Number.isFinite(spread) && spread > 0 ? spread : null,
    cam,
    overlays,
  };
}

export function isViewHash(hash) {
  return new RegExp(`^#\\/?${VIEW_PREFIX}\\/`).test(String(hash || ''));
}
